"use client";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/molecules/Form";
import { cn } from "@/lib/utils";
import * as React from "react";
import { useFormContext } from "react-hook-form";
import { Combobox, ComboboxProps } from "./Combobox";
import { ComboboxTrigger } from "./ComboboxTrigger";

export type ComboboxFieldProps = Omit<
  ComboboxProps,
  "value" | "onValueChange"
> & {
  name: string;
  label?: React.ReactNode;
  subcopy?: React.ReactNode;
  note?: React.ReactNode;
  readOnly?: boolean;
  required?: boolean;
};

export const ComboboxField = ({
  name,
  label,
  subcopy,
  note,
  options,
  renderTrigger,
  disabled,
  fullWidth,
  readOnly,
  emptyMessage = "No options found.",
  triggerProps,
  filterProps,
  testId,
  required,
}: ComboboxFieldProps) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <FormItem className="tw-flex tw-flex-col" disabled={disabled}>
            {!!label && (
              <FormLabel
                className={cn({
                  "tw-pointer-events-none": readOnly || disabled,
                })}
                subcopy={subcopy}
                required={required}
              >
                {label}
              </FormLabel>
            )}

            <Combobox
              testId={testId}
              options={options}
              value={field.value}
              onValueChange={field.onChange}
              renderTrigger={(selectedOption) => {
                return (
                  <FormControl>
                    {!!renderTrigger ? (
                      renderTrigger(selectedOption, field)
                    ) : (
                      <ComboboxTrigger
                        ref={field.ref}
                        fullWidth={fullWidth}
                        readOnly={readOnly}
                        disabled={disabled}
                        value={field.value}
                        {...(triggerProps || {})}
                      >
                        {selectedOption?.label || triggerProps?.placeholder}
                      </ComboboxTrigger>
                    )}
                  </FormControl>
                );
              }}
              emptyMessage={emptyMessage}
              disabled={disabled}
              filterProps={filterProps}
            />

            <FormMessage>{note}</FormMessage>
          </FormItem>
        );
      }}
    />
  );
};
